import { useEffect, useState, useCallback } from 'react';
import {
  institutionDepartments,
  institutionUsers,
  type Department,
  type User,
  type CreateDepartmentInput,
  type UpdateDepartmentInput,
} from '@/services/institution';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import styles from './DepartmentsPage.module.css';

type ModalMode = 'create' | 'edit' | 'view-users' | null;

export function DepartmentsPage() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [departmentUsers, setDepartmentUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<Partial<CreateDepartmentInput>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await institutionDepartments.list();
      if (response.success && response.data) {
        setDepartments(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await institutionUsers.list({ perPage: 100 });
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, [fetchDepartments, fetchUsers]);

  const filteredDepartments = departments.filter((dept) => {
    const search = searchQuery.toLowerCase();
    return (
      dept.name.toLowerCase().includes(search) ||
      dept.code.toLowerCase().includes(search) ||
      (dept.description?.toLowerCase() || '').includes(search)
    );
  });

  function openCreateModal() {
    setFormData({
      code: '',
      name: '',
      description: '',
      parentId: '',
      headUserId: '',
    });
    setError(null);
    setModalMode('create');
  }

  function openEditModal(dept: Department) {
    setSelectedDepartment(dept);
    setFormData({
      code: dept.code,
      name: dept.name,
      description: dept.description || '',
      parentId: dept.parentId || '',
      headUserId: dept.headUserId || '',
    });
    setError(null);
    setModalMode('edit');
  }

  async function openViewUsersModal(dept: Department) {
    setSelectedDepartment(dept);
    setDepartmentUsers([]);
    setModalMode('view-users');

    try {
      const response = await institutionDepartments.getUsers(dept.id);
      if (response.success && response.data) {
        setDepartmentUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch department users:', err);
    }
  }

  function closeModal() {
    setModalMode(null);
    setSelectedDepartment(null);
    setDepartmentUsers([]);
    setFormData({});
    setError(null);
  }

  async function handleCreateDepartment(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.code || !formData.name) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const input: CreateDepartmentInput = {
        code: formData.code!,
        name: formData.name!,
        description: formData.description || undefined,
        parentId: formData.parentId || undefined,
        headUserId: formData.headUserId || undefined,
      };

      const response = await institutionDepartments.create(input);
      if (response.success) {
        closeModal();
        fetchDepartments();
      } else {
        setError(response.error?.message || 'Failed to create department');
      }
    } catch (err) {
      setError('An error occurred while creating the department');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateDepartment(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedDepartment) return;

    setSubmitting(true);
    setError(null);

    try {
      const input: UpdateDepartmentInput = {
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        parentId: formData.parentId || undefined,
        headUserId: formData.headUserId || undefined,
      };

      const response = await institutionDepartments.update(selectedDepartment.id, input);
      if (response.success) {
        closeModal();
        fetchDepartments();
      } else {
        setError(response.error?.message || 'Failed to update department');
      }
    } catch (err) {
      setError('An error occurred while updating the department');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteDepartment(dept: Department) {
    if (!confirm(`Are you sure you want to delete the department "${dept.name}"?`)) {
      return;
    }

    try {
      const response = await institutionDepartments.delete(dept.id);
      if (response.success) {
        fetchDepartments();
      } else {
        alert(response.error?.message || 'Failed to delete department');
      }
    } catch (err) {
      alert('An error occurred while deleting the department');
    }
  }

  function getParentName(parentId: string | null): string {
    if (!parentId) return '-';
    const parent = departments.find((d) => d.id === parentId);
    return parent?.name || '-';
  }

  function getHeadName(headUserId: string | null): string {
    if (!headUserId) return '-';
    const head = users.find((u) => u.id === headUserId);
    return head ? `${head.firstName} ${head.lastName}` : '-';
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Departments</h1>
          <p className={styles.subtitle}>Manage organizational departments and structure</p>
        </div>
        <Button onClick={openCreateModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add Department
        </Button>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search departments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.grid}>
        {loading ? (
          <div className={styles.loading}>Loading departments...</div>
        ) : filteredDepartments.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 21h18M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7H3l2-4h14l2 4M5 21V10.85M19 21V10.85M9 21v-4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4" />
            </svg>
            <p>No departments found</p>
          </div>
        ) : (
          filteredDepartments.map((dept) => (
            <div key={dept.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7H3l2-4h14l2 4M5 21V10.85M19 21V10.85M9 21v-4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4" />
                  </svg>
                </div>
                <div className={styles.cardActions}>
                  <button
                    className={styles.cardActionBtn}
                    onClick={() => openViewUsersModal(dept)}
                    title="View users"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </button>
                  <button
                    className={styles.cardActionBtn}
                    onClick={() => openEditModal(dept)}
                    title="Edit"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    className={`${styles.cardActionBtn} ${styles.cardActionDanger}`}
                    onClick={() => handleDeleteDepartment(dept)}
                    title="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{dept.name}</h3>
                <span className={styles.cardCode}>{dept.code}</span>
                {dept.description && (
                  <p className={styles.cardDescription}>{dept.description}</p>
                )}
              </div>
              <div className={styles.cardMeta}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Parent:</span>
                  <span className={styles.metaValue}>{getParentName(dept.parentId)}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Head:</span>
                  <span className={styles.metaValue}>{getHeadName(dept.headUserId)}</span>
                </div>
              </div>
              <div className={styles.cardFooter}>
                <span
                  className={`${styles.statusBadge} ${
                    dept.isActive ? styles.statusActive : styles.statusInactive
                  }`}
                >
                  {dept.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Department Modal */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Add New Department' : 'Edit Department'}
      >
        <form onSubmit={modalMode === 'create' ? handleCreateDepartment : handleUpdateDepartment}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.formRow}>
            <Input
              label="Department Code"
              value={formData.code || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
              required
              hint="Unique identifier (e.g., HR, FIN, IT)"
            />
            <Input
              label="Department Name"
              value={formData.name || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <Input
            label="Description"
            value={formData.description || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          />

          <Select
            label="Parent Department"
            value={formData.parentId || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, parentId: e.target.value }))}
          >
            <option value="">No Parent (Top Level)</option>
            {departments
              .filter((d) => d.id !== selectedDepartment?.id)
              .map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
          </Select>

          <Select
            label="Department Head"
            value={formData.headUserId || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, headUserId: e.target.value }))}
          >
            <option value="">Select Head</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName} ({u.email})
              </option>
            ))}
          </Select>

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : modalMode === 'create' ? 'Create Department' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Department Users Modal */}
      <Modal
        isOpen={modalMode === 'view-users'}
        onClose={closeModal}
        title={`Users in ${selectedDepartment?.name}`}
      >
        <div className={styles.usersList}>
          {departmentUsers.length === 0 ? (
            <div className={styles.emptyUsers}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <p>No users in this department</p>
            </div>
          ) : (
            departmentUsers.map((user) => (
              <div key={user.id} className={styles.userItem}>
                <div className={styles.userAvatar}>
                  {user.firstName[0]}
                  {user.lastName[0]}
                </div>
                <div className={styles.userInfo}>
                  <span className={styles.userName}>
                    {user.firstName} {user.lastName}
                  </span>
                  <span className={styles.userEmail}>{user.email}</span>
                  {user.jobTitle && (
                    <span className={styles.userTitle}>{user.jobTitle}</span>
                  )}
                </div>
                <span
                  className={`${styles.userStatus} ${
                    user.isActive ? styles.statusActive : styles.statusInactive
                  }`}
                >
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))
          )}
        </div>
        <div className={styles.modalActions}>
          <Button type="button" variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
}
